/** Gaps with the Angular console closed on 2026-10-23. Same keys as `gestes.fr.ts`. */
export const projectSettingsEn = {
  short: 'Settings',
  title: 'Project settings',
  subtitle: 'Name, description, badge and labels of project {{project}}.',
  labelsHelp: 'One label per line, as key=value.',
  saved: 'Project saved.',
  errors: {
    labels: 'One label per line, as key=value (key: letters, digits, “.”, “_”, “-” or “/”).',
  },
  danger: {
    title: 'Delete the project',
    description:
      'Its jobs, its workflows, their runs and the permissions held on the project are deleted with it. The organization’s buckets, warehouses and workspaces remain.',
  },
  delete: 'Delete project',
  confirmDelete:
    'Delete project “{{name}}”? Its jobs, workflows, runs and permissions will be deleted. This cannot be undone.',
  deleted: 'Project “{{name}}” deleted.',
} as const;

export const projectMetricsEn = {
  short: 'Metrics',
  title: 'Project metrics',
  subtitle:
    'CPU and memory used by the pods of project {{project}} (runs of its jobs and workflows), sampled every 30 s.',
  window: 'Period',
  windows: { hour: 'Last hour', day: '24 hours', week: '7 days' },
  help: 'Sum over the project’s pods at each sample, then the average per step. Beyond 7 days, one point per hour. An empty series means no pod of the project ran during the period.',
} as const;

export const bucketManageEn = {
  new: 'New bucket',
  newTitle: 'Declare a bucket',
  newSubtitle:
    'An existing S3 bucket (Ceph RGW, MinIO, AWS…) or an Azure Blob container. The access keys are stored, encrypted, in the organization’s vault and never shown again.',
  fields: {
    name: 'Name',
    nameHelp: 'Name shown in the console.',
    description: 'Description',
    type: 'Storage',
    path: 'Location',
    pathHelp: 's3://bucket-name, optionally followed by a prefix.',
    endpoint: 'S3 endpoint',
    endpointHelp: 'Address of the S3 service, for example https://s3.fr-par.example.com.',
    region: 'Region',
    accessKey: 'Access key',
    secretKey: 'Secret key',
    azurePath: 'Azure container',
    azurePathHelp:
      'https://<account>.blob.core.windows.net/<container> or abfss://<container>@<account>.dfs.core.windows.net, optionally followed by a prefix.',
    azureAuth: 'Authentication',
    azureAuthHelp:
      'The storage account key, or an Entra ID service principal granted “Storage Blob Data Contributor” on the account or the container.',
    accountKey: 'Storage account key',
    tenantId: 'Entra ID tenant',
    clientId: 'Client (application) ID',
    clientSecret: 'Client secret',
    labels: 'Labels',
  },
  typeOptions: {
    s3: 'S3 (Ceph RGW, MinIO, AWS…)',
    azure: 'Azure Blob Storage',
  },
  azureAuthOptions: {
    accountKey: 'Storage account key',
    servicePrincipal: 'Entra ID service principal',
  },
  errors: {
    path: 'Expected format: s3://bucket-name, optionally followed by a prefix.',
    azurePath:
      'Expected format: https://<account>.blob.core.windows.net/<container> or abfss://<container>@<account>.dfs.core.windows.net, without “.” or “..”.',
    tenantId: 'A GUID or a domain name (contoso.onmicrosoft.com) is expected.',
    endpoint: 'An http:// or https:// address is expected.',
  },
  created: 'Bucket “{{name}}” declared.',
  saved: 'Bucket saved.',
  definitionNote:
    'The location, the type and the access keys cannot be changed (the backend refuses it): declare another bucket.',
  delete: 'Delete bucket',
  confirmDelete:
    'Delete bucket “{{name}}”? Its declaration and keys leave graal. The files of an external S3 bucket stay with their host; a managed bucket is emptied, then deleted.',
  deleted: 'Bucket “{{name}}” deleted.',
  tabs: { definition: 'Definition' },
  folder: {
    new: 'New folder',
    title: 'New folder',
    description:
      'An S3 folder only exists through its objects: graal creates it in {{path}} by uploading an empty “keep” file, like the former console.',
    name: 'Folder name',
    nameHelp: 'A single level, without “/”.',
    invalid: 'A name without “/”, without leading or trailing spaces, other than “.” and “..”.',
    submit: 'Create folder',
    created: 'Folder {{path}} created.',
  },
} as const;

export const warehouseManageEn = {
  new: 'New warehouse',
  newTitle: 'New SQL warehouse',
  newSubtitle:
    'A schema of the platform’s Trino, the connection to an existing database, or a PostgreSQL, MySQL or MariaDB database managed by graal. A password is stored in the organization’s vault and never shown again.',
  fields: {
    name: 'Name',
    description: 'Description',
    engine: 'Engine',
    engineHelp:
      'Trino: a schema of the platform engine (unavailable if the installation has none). The others: an existing database, reached by graal-api. DB2: IBM’s driver is not shipped with graal (IBM license), the operator of the installation provides it — without it, “feature unavailable”.',
    host: 'Host',
    port: 'Port',
    portHelp: 'Empty: the engine’s default port (5432, 3306, 1521, 1433 or 50000 for DB2).',
    database: 'Database',
    schema: 'Schema',
    sslMode: 'Encryption',
    username: 'User',
    password: 'Password',
    labels: 'Labels',
    account: 'Snowflake account',
    accountHelp:
      'Account identifier: orgname-accountname, or the account locator with its region (xy12345.eu-west-1). graal-api reaches <account>.snowflakecomputing.com over verified HTTPS.',
    computeWarehouse: 'Compute warehouse',
    computeWarehouseHelp: 'The virtual warehouse of the session (COMPUTE_WH…). Empty: the user’s default.',
    role: 'Role',
    roleHelp: 'The role of the session; prefer a read-only role. Empty: the user’s default.',
    authentication: 'Authentication',
    privateKey: 'Private key',
    privateKeyHelp:
      'Unencrypted PKCS#8 key, in PEM (openssl pkcs8 -topk8 -nocrypt). It is stored in the organization’s vault and never shown again.',
  },
  engineOptions: {
    trino: 'Platform Trino (managed schema)',
    postgresql: 'PostgreSQL',
    mysql: 'MySQL',
    mariadb: 'MariaDB',
    oracle: 'Oracle (external database)',
    sqlserver: 'SQL Server (external database)',
    db2: 'IBM DB2 (external database)',
    snowflake: 'Snowflake (external account)',
  },
  sslModes: {
    default: 'Driver default',
    disable: 'Disabled',
    require: 'Encrypted, certificate not verified',
    verify: 'Encrypted, certificate verified',
  },
  authentications: {
    password: 'Password',
    key_pair: 'Key pair',
  },
  errors: {
    host: 'A host name or IP address, without scheme or port.',
    port: 'A port between 1 and 65535.',
    account: 'Snowflake account identifier: letters, digits, hyphens, underscores and dots.',
    identifier: 'An unquoted Snowflake identifier: letters, digits, _ and $.',
  },
  created: 'Warehouse “{{name}}” created.',
  saved: 'Warehouse saved.',
  definitionNote: 'The engine and the connection cannot be changed here: declare another warehouse.',
  delete: 'Delete warehouse',
  confirmDelete:
    'Delete warehouse “{{name}}”? Its declaration leaves graal. The tables of a Trino schema are kept; an external database is not touched.',
  deleted: 'Warehouse “{{name}}” deleted.',
  tabs: { definition: 'Definition' },
} as const;

export const preferencesEn = {
  title: 'Preferences',
  subtitle:
    'Settings of this console, kept in this browser (local storage): they do not follow your account from one device to another.',
  theme: {
    title: 'Appearance',
    description: 'Dark by default. “System” follows your device setting and changes with it.',
  },
  language: {
    title: 'Language',
    description: 'Language of the interface. Names and descriptions entered in graal are not translated.',
    names: { fr: 'Français', en: 'English' },
  },
  stored: 'Applied and kept in this browser.',
} as const;
